import React, {useEffect}         from 'react'

import { MoreLess }                         from '../../lib/iprs-react-library/src/index.js';
import AppTypeImage                         from './AppTypeImage';
import { PS_APPOINTMENT }                   from '../App/Reducer';
import { EditButton, PlusInput }            from '../../lib/iprs-react-library/src/index.js';
import userLoggedInHasRole                  from '../Util/userLoggedInHasRole';

/**
 * React component to display a selectable list of Appointment types. This is 
 * used in the sidebar. The list divides into groups. Groups can be expanded
 * and closed by clicking. Only one group is open at a time. A group can be
 * closed when it contains the selected app type but, when another group is
 * opened and closed, the group with the selected app type re-opens. If the user
 * clicks the go to app type calendar button in the booking process, then the
 * correct group opens up. 
 * @param {API} props.api The API object for comms with IPRS network API 
 * @param {function} props.eventDispatch The main application event dispatch 
 * function. See the main App
 * @param {Object} props.state The main application state object.
 * @returns {JSX}
 */
const AppTypeSelectList = ({api, eventDispatch, state}) => {
    
    /* Diary Managers can edit and add appointment types from the list */
    const hasDiaryManagerPerm = userLoggedInHasRole(api, 'Diary Manager');
    
    const { appType: { TypesInGroups = {}, Groups = [], appointmentTypeID: selectedAppTypeID, selectedGroupTitle, appointmentTypes = {} }, pageState } = state

    // the app type currently selected and stored in the app state as appointmentTypeID
    const selectedAppType = appointmentTypes.find(at => selectedAppTypeID === at.AppointmentTypeID);
    // the group ID of the currently selected app type if one is selected
    const selectedGroupID       = selectedAppType?.GroupID;
    // the group identified by selectedGroupID
    const selectedAppTypeGroup = Groups.find(g => g.GroupID === selectedGroupID);
    // the name of the group selectedAppTypeGroup
    const selectedAppTypeTitle = selectedAppTypeGroup?.GroupTitle;

    /* finally, we have the title of the group either open by clicking 
     open the group or by selecting an app type within the group, perhaps
     as part of the booking process which happens outside this component */
    const openGroupTitle = selectedGroupTitle?? selectedAppTypeTitle;


    const isLoggedIn = api.isLoggedIn();

    useEffect(() => {

        if(!isLoggedIn) {return;}

        /* as this class is loaded in the sidebar, it loads all the app types
         * and passess them back to the application so other parts like the 
         * booking system don't have to go back to the API for this data.
         */
        api.AppointmentTypeList()
            .then(r => r.apiResult)
            .then(apiResult => {

                /* values is grouped by group name so unbundle the groups and concatinate */
                const types = Object.values(apiResult.Types).reduce((prev, curr) => [...prev, ...curr], []);
                const appTypeOb = {
                    appType: {
                        appointmentTypes: types,
                        Groups: apiResult.Groups,
                        TypesInGroups: apiResult.Types
                    }
                };

                eventDispatch(appTypeOb);
            })
            .catch(error => {
                console.log('Error ' + error)
            })

    }, [api, isLoggedIn, eventDispatch]);

    /* when there's a change in app type selected - which can be because the app type
     was clicked or because it was selected as part of the booking process - make sure
     the correct group is expanded. */
    useEffect(() => {
        if (selectedAppTypeTitle) {
            eventDispatch({ appType: { selectedGroupTitle: selectedAppTypeTitle } });
        }
    }, [selectedAppTypeTitle, eventDispatch]);

    const typeEntries = Object.entries(TypesInGroups);

    const clickHandler = e => {

        e.preventDefault?.();

        // are we opening or closing a group
        const groupOnOff = e.target.value;
        const groupName = e.target.name;
        let title;
        if (groupOnOff) {
            title = groupName;
        } else if (selectedAppTypeTitle === selectedGroupTitle || pageState !== PS_APPOINTMENT) {
            title = '__null__';
        } else {
            title = selectedAppTypeTitle;
        }
        eventDispatch({ appType: { selectedGroupTitle: title } })
        
    }

    const appTypeClick = appTypeID => eventDispatch({ appType: { appointmentTypeID: appTypeID } });

    const onEditClick = appTypeID => eventDispatch({
        appType: {
            appointmentTypeID: appTypeID,
            overlay: 'editAppType'
        }
    });

    const onCreateClick = e => {

        e.preventDefault();

        eventDispatch({
            appType: {
                appointmentTypeID: 0,
                overlay: 'editAppType',
                NewTitle: e.target.value
            }
        });
    }

    return (
        <>
        <ul   >
            {
                typeEntries.map(t => <AppTypeGroup
                    key={t[0]}
                    groupName={t[0]}
                    appTypes={t[1]}
                    eventDispatch={eventDispatch}
                    pageState={pageState}
                    selectedAppTypeID={selectedAppTypeID}
                    openGroupTitle={openGroupTitle}
                    onGrpClick={clickHandler}
                    onAppTypeClick={appTypeClick}
                    onEditClick={onEditClick}
                    hasDiaryManagerPerm={hasDiaryManagerPerm}
                />)
            }
            </ul>
            <div style={{ marginBottom: '1em' }}>
            { hasDiaryManagerPerm &&
                <PlusInput onClick={onCreateClick} />
            }
            </div>
            </>
    )
}

const AppTypeGroup = ({ groupName, appTypes, pageState,
    selectedAppTypeID, openGroupTitle, onGrpClick, onAppTypeClick,
    onEditClick, hasDiaryManagerPerm }) => {

    const show = groupName === openGroupTitle;

    const handleGrpClick = e => {
        onGrpClick({ target: { name: groupName, value: !show } });
    }

    return <li>
        <div className='sideMenuLink'>
            <h3 onClick={handleGrpClick} className="sideMenuLink-anchor"  >{groupName} </h3>
            <nav className="sideMenuButtons">
                <MoreLess onClick={handleGrpClick} name={groupName} value={show} />
            </nav>
        </div>
        <div style={{ maxHeight: show ? 'fit-content' : '0', transition: 'all .1s', overflow: 'hidden' }} >
        <ul  >
                {
                    appTypes.map(a => <AppType
                        appTypeID={a.AppointmentTypeID}
                        key={a.AppointmentTypeID}
                        title={a.Title}
                        pageState={pageState}
                        selectedAppTypeID={selectedAppTypeID}
                        onAppTypeClick={onAppTypeClick}
                        onEditClick={onEditClick}
                        hasDiaryManagerPerm={hasDiaryManagerPerm} />)
        }
            </ul>
        </div>
    </li>
}


/*
    * Component to represent a single type in the select list
    */
const AppType = ({ appTypeID, title, icon, pageState, selectedAppTypeID,
    onAppTypeClick, onEditClick, hasDiaryManagerPerm }) => {

    const active = 'appointment' === pageState && appTypeID === selectedAppTypeID

    const handleAppTypeClick = e => {
        e.preventDefault();
        onAppTypeClick(appTypeID);
    }

    const handleAppTypeEdit = e => {
        e.preventDefault();
        onEditClick(appTypeID);
    }

    return (
        <li className={'sideMenuLink ' + (active ? 'active' : '')} >
            <a href='.' name={'AppType'+appTypeID} id={appTypeID} onClick={handleAppTypeClick} className='sideMenuLink-anchor' >
                {false && <AppTypeImage icon={icon} />}
                {title}
            </a>
            <div className='sideMenuButtons' >
                {hasDiaryManagerPerm &&
                <EditButton onClick={handleAppTypeEdit} value='Edit Appointment Type' 
                    title='Edit Appointment Type' enabled={hasDiaryManagerPerm} />
                }
            </div>
        </li>
    )
}

export default AppTypeSelectList